import {PhoneSelectedIcon} from "../../../../assets/icons/PhoneSelectedIcon";
import {formatPhoneNumber} from "../../../../utils/friendlyPhone";
import {MobileMoneyIcon} from "../../../../assets/icons/MobileMoneyIcon";
import {MessagingCostIcon} from "../../../../assets/icons/MessagingCostIcon";
import {WhatsappRoundIcon} from "../../../../assets/icons/WhatsappRoundIcon";
import useAppContext from "../../../../hooks/useAppContext";
import "./achievements.css";
import useUserContext from "../../../../hooks/useUserContext";
import useToast from "../../../../hooks/useToast";
import {useEffect, useState} from "react";
import {EMPTY_MESSAGE_PRICING, getMessagePricing} from "../../../../utils/getMessagePricing";
import {ToastSkin} from "../../../../context/ToastContext";
import {getTwilioSender} from "../../../../api/getTwilioSender";
import {Tooltip} from "antd";
import {HeartbeatIcon} from "../../../../assets/icons/HeartbeatIcon";
import {MessagesIcon} from "../../../../assets/icons/MessagesIcon";
import Loader from "../../../loader/Loader";
import {useNavigate} from "react-router-dom";
import {isFreePricingPlanId} from "../../../../consts/freePricingPlans";

interface AchievementsProps {
    iconsFill?: string;
    showWhatsappAccountStatus?: boolean;
}

const Achievements = ({ iconsFill = '#7f84b2', showWhatsappAccountStatus = false }: AchievementsProps) => {
    const { data: { waba, shop }} = useAppContext();
    const wabaId = waba?.data?.waba?.whatsappBusinessAccountId;
    const phones = waba?.data?.waba?.phones;
    const phone = phones?.length ? phones[0] : null;
    const { selectedDomain } = useUserContext();
    const { showToast } = useToast();
    const [messagePrices, setMessagePrices] = useState(EMPTY_MESSAGE_PRICING);
    const [whatsappAccountState, setWhatsappAccountState] = useState<{title: string, description?: string, messagesLimit?: number | string}>({ title: '', description: undefined, messagesLimit: '' });
    const navigate = useNavigate();
    const isFreePlan = isFreePricingPlanId(shop?.data?.pricingPlan?.id);

    const loadMessagePrices = async () => {
        const phones = waba?.data?.waba?.phones;
        const phone = phones?.length ? phones[0] : null;
        if(phone) {
            setMessagePrices(await getMessagePricing({
                iso: phone.iso,
                selectedDomain,
                onError: (message: string) => showToast({text: message, skin: ToastSkin.error})
            }));
        }
    }

    const loadWhatsappAccountState = async () => {
        if(selectedDomain && phone && wabaId  && showWhatsappAccountStatus) {
            const response = await getTwilioSender(selectedDomain);
            if(response.data.error) {
                showToast({ skin: ToastSkin.error, text: 'Failed to load Whatsapp account state' });
            } else {
                setWhatsappAccountState({
                    title: response.data.data.status.toLowerCase(),
                    description: response.data.data?.offline_reasons?.[0]?.message,
                    messagesLimit: response.data.data?.properties?.messaging_limit
                })
            }
        }
    }

    useEffect(() => {
        loadMessagePrices();
        loadWhatsappAccountState();
    }, [selectedDomain, phone, wabaId, showWhatsappAccountStatus]);

    return (
        <div className="achievements">
            <div className="achievement in-mobile">
                <div className="flex achievement-title">
                    <div className="icon"><PhoneSelectedIcon width={36} color={iconsFill} /></div>
                    <div className="title">Phone Number:</div>
                </div>
                <div className="value">{phone ? formatPhoneNumber(phone.phoneNumber) : ''}</div>
            </div>
            {isFreePlan ? null : <div className="achievement">
                <div className="flex achievement-title">
                    <div className="icon"><MobileMoneyIcon width={36} color={iconsFill} /></div>
                    <div className="title">Cost:</div>
                </div>
                <div className="value">{shop?.data?.pricingPlan?.id === 1 && phone ? `$${Math.round(phone.monthlyCost*100)/100}` : shop?.data?.pricingPlan?.isPhoneNumberIncluded ? '$0' : phone ? '$19' : '-'}</div>
            </div>}
            {isFreePlan ? null : <div className="achievement">
                <div className="flex achievement-title">
                    <div className="icon fill"><MessagingCostIcon width={36} color={iconsFill}/></div>
                    <div className="title">Domestic Message:</div>
                </div>
                <div className="value">{phone && messagePrices.marketing ? `$${messagePrices.marketing}` : '-'}</div>
            </div>}
            {wabaId ? <div className="achievement in-mobile">
                <div className="flex achievement-title">
                    <div className="icon"><WhatsappRoundIcon width={36} color={iconsFill} /></div>
                    <div className="title">Whatsapp Account:</div>
                </div>
                <div className="value">{wabaId ? 'Connected' : 'Not Connected'}</div>
            </div> : null}
            {wabaId ? <div className="achievement in-mobile">
                <div className="flex achievement-title">
                    <div className="icon"><HeartbeatIcon width={36} color={iconsFill} /></div>
                    <div className="title">Whatsapp Account Status:</div>
                </div>
                <div className="value">{whatsappAccountState?.title ? <Tooltip title={whatsappAccountState?.description || ''}>{whatsappAccountState?.title}</Tooltip> : <span><Loader size={24} /></span>}</div>
            </div> : null}
            {whatsappAccountState?.messagesLimit && whatsappAccountState?.messagesLimit !== "Unavailable" ?
                <div className="achievement">
                    <div className="flex achievement-title">
                        <div className="icon fill"><MessagesIcon width={36} color={iconsFill} /></div>
                        <div className="title">Whatsapp Messages Limit:</div>
                    </div>
                    <div className="value">{whatsappAccountState?.messagesLimit}</div>
                </div>
                : null}
            {showWhatsappAccountStatus && whatsappAccountState?.title && whatsappAccountState?.title.toLowerCase() !== 'online' ? <div className="docs-ref">
                Looks like your account isn’t connected. <span onClick={() => navigate('/docs#whatsapp-connection-troubleshooting')}>Learn more here.</span>
            </div> : null}
        </div>
    );
}

export default Achievements;